import type { DebitModification, MemberWithRatings, Rating } from "../types";

export interface MemberDebitBreakdown {
  rxDebits: number;
  conditionDebits: number;
  build: number;
  totalDebits: number;
  uwDollars: number;
}

/**
 * Effective debits for a rating (product plan 3.3): override if set, else base
 * times multiplier, else base. A modification carrying both values is invalid.
 */
export function effectiveRatingDebits(rating: Rating, modification: DebitModification | null): number {
  if (!modification || !modification.isActive) return rating.baseDebits;
  const hasOverride = modification.overrideDebits !== null;
  const hasMultiplier = modification.multiplier !== null;
  if (hasOverride && hasMultiplier) {
    throw new Error(`Debit modification ${modification.id} sets both a multiplier and an override; exactly one is allowed.`);
  }
  if (hasOverride) return modification.overrideDebits as number;
  if (hasMultiplier) return Math.round(rating.baseDebits * (modification.multiplier as number));
  return rating.baseDebits;
}

export function activeModificationFor(ratingId: number, modifications: readonly DebitModification[]): DebitModification | null {
  return modifications.find((modification) => modification.ratingId === ratingId && modification.isActive) ?? null;
}

/**
 * Member totals over non-excluded assigned ratings (product plan 4.5). Rx debits
 * are medication ratings; condition debits are everything else including the
 * synthetic Doubled Build and Doubled Hypertension rows.
 */
export function memberDebitTotals(member: MemberWithRatings, build: number): MemberDebitBreakdown {
  const active = member.assignedRatings.filter((rating) => !rating.isExcluded);
  const rxDebits = active.filter((rating) => rating.isMedication).reduce((sum, rating) => sum + rating.assignedDebits, 0);
  const conditionDebits = active.filter((rating) => !rating.isMedication).reduce((sum, rating) => sum + rating.assignedDebits, 0);
  const uwDollars = active.reduce((sum, rating) => sum + (rating.uwDollars ?? 0), 0);

  return { rxDebits, conditionDebits, build, totalDebits: rxDebits + conditionDebits + build, uwDollars };
}
